import type { ReactNode } from "react"
import { useLocation } from "react-router-dom"
import { useTenant } from "@edemics/contexts/tenant-provider"
import { RouteLoadingFallback } from "./LoadingFallback"

/**
 * R2 — Tenant surface gate.
 *
 * Sits INSIDE the router (needs useLocation) and OUTSIDE the AuthGuard so
 * it fires before login. Decides how to render the app based on how we got
 * here:
 *
 *   • On a tenant subdomain, lookup pending      → route-loading skeleton.
 *   • On a tenant subdomain, lookup 404'd        → "Tenant not found" page
 *                                                  with a link to the apex
 *                                                  login (host-relative to
 *                                                  the current apex, R4-safe).
 *   • On apex (subdomain === null), or lookup OK → children (normal routing
 *                                                  proceeds; LoginPage
 *                                                  handles the apex-login
 *                                                  redirect to the tenant
 *                                                  subdomain itself).
 *
 * Public routes that MUST render without a tenant (the tenant-lookup UX
 * itself, or a not-found landing) are exempted below by exact-path match.
 */
const PUBLIC_APEX_ROUTES = new Set<string>([
  "/", // apex root → LoginPage redirect target
  "/login",
  "/register",
  "/otp-verification",
  "/guest-access",
])

export function TenantGuard({ children }: { children: ReactNode }) {
  const { subdomain, hostname, tenant, loading, error } = useTenant()
  const location = useLocation()

  // Wave S (2026-09-10) — the "tenant surface" test is `hostname` now,
  // not `subdomain`. A BYO custom-domain host (portal.acme.edu) has
  // hostname set but subdomain=null; the previous shape would have
  // fallen straight through to `return children` and skipped the
  // tenant-lookup wait, showing the login form before we knew whether
  // the domain was a real tenant.
  if (hostname === null) {
    return <>{children}</>
  }

  // On a tenant host, wait for the lookup before rendering. Skeleton
  // keeps the first paint honest — we don't want to flash the login
  // form on a valid tenant while the lookup resolves.
  if (loading && !tenant) {
    return <RouteLoadingFallback />
  }

  // 404 / lookup failure. Public routes (login, register, admission apply)
  // stay accessible so a support tech can still get in; everything else
  // renders the not-found card.
  if (error && !tenant) {
    if (PUBLIC_APEX_ROUTES.has(location.pathname)) return <>{children}</>
    return (
      <TenantNotFound identifier={subdomain ?? hostname} message={error} />
    )
  }

  return <>{children}</>
}

function TenantNotFound({
  identifier,
  message,
}: {
  identifier: string
  message: string
}) {
  // The apex-relative sign-in link — hard-coded to edemics.com to match
  // APEXES[0] in @edemics/lib/subdomain. R4 will replace this with a
  // host-derived link so custom-domain tenants get a matching apex.
  const apexLogin = "https://edemics.com/login"
  return (
    <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-3xl" aria-hidden>?</span>
        </div>
        <h1 className="text-xl font-semibold text-light-text-primary dark:text-white">
          Tenant "{identifier}" not found
        </h1>
        <p className="text-sm text-light-text-secondary dark:text-white/70">
          {message}. Check the spelling of the address, or sign in on the
          main site.
        </p>
        <a
          href={apexLogin}
          className="inline-flex items-center justify-center gap-2 bg-[#8C57FF] hover:bg-[#7C3AED] text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
        >
          Sign in on edemics.com
        </a>
      </div>
    </div>
  )
}
